import { debounce } from "lodash";
import { AutoCompleteSelectParams } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import {
  InputNumber,
  InputNumberValueChangeParams,
} from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { OverlayPanel } from "primereact/overlaypanel";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { setRows } from "store/slices/serviceDetails";
import { ProcedureRow } from "types/CreateOrder";
import { IProcedure } from "types/Procedure";
import { IProviderProcedure } from "types/ProviderProcedure";

import { buildSelectedProceduresObject } from "../../builders";
import {
  autoCompleteHandleOnUnSelectProcedureNew,
  quantityChangeHandleOnSelectProcedure,
} from "../../handlers";

// Define benefit selection options
const benefitOptions = [
  { value: null, label: "Default Benefit" },
  { value: "SELF_PAY", label: "Not Covered / Self-Pay" },
  { value: "COINSURANCE_APPLIES", label: "Co-insurance Applies" },
];

const benefitOptionsGFE = [
  { value: null, label: "Default Benefit" },
  { value: "SELF_PAY", label: "Not Covered / Self-Pay" },
  { value: "COINSURANCE_APPLIES", label: "Co-insurance Applies" },
  { value: "REQUIRES_ABN", label: "Requires ABN" },
];

const preventiveOption = { value: "PREVENTIVE", label: "Preventive" };

export function ProcedureInfoColumn({
  row,
  rows,
  autoCompleteValue,
  dispatch,
  selectedProcedures,
  procedures,
  providerProcedures,
  isGFE,
}: {
  row: ProcedureRow;
  rows: ProcedureRow[];
  autoCompleteValue: any;
  dispatch: any;
  selectedProcedures: string[];
  procedures: IProcedure[];
  providerProcedures: IProviderProcedure[];
  isGFE: boolean;
}) {
  const [showError, setShowError] = useState(!row.description.trim().length);
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState<number | null>(row.quantity);
  const [committedQuantity, setCommittedQuantity] = useState<number | null>(
    row.quantity
  );
  const op = useRef<OverlayPanel>(null);

  useEffect(() => {
    setDescription(row.description);
  }, [row.description]);

  useEffect(() => {
    setQuantity(row.quantity);
    setCommittedQuantity(row.quantity);
  }, [row.quantity]);

  const debouncedQuantityUpdate = useCallback(
    debounce((value: number | null) => {
      if (value === null) {
        return;
      }

      const codeToDelete = row.cptCode;
      const repammedAutocompleteValue = autoCompleteValue.map(
        (value: string) => {
          return buildSelectedProceduresObject([value])[0];
        }
      );
      const procedureDefaultDescription = repammedAutocompleteValue.find(
        (value: any) => value.code === codeToDelete
      )?.cptDescription;

      if (value === 0) {
        autoCompleteHandleOnUnSelectProcedureNew({
          e: {
            value: `${row.cptCode} - ${procedureDefaultDescription}`,
          } as AutoCompleteSelectParams,
          autoCompleteValue,
          rows,
          dispatch,
          selectedProcedures,
        });
        return;
      }

      quantityChangeHandleOnSelectProcedure({
        e: {
          value: `${row.cptCode} - ${row.description}`,
        } as AutoCompleteSelectParams,
        selectedProcedures,
        procedures,
        providerProcedures,
        rows,
        dispatch,
        newValue: value || 1,
        row,
      });
    }, 500),
    [
      row.cptCode,
      row.description,
      autoCompleteValue,
      rows,
      dispatch,
      selectedProcedures,
      procedures,
      providerProcedures,
      row,
    ]
  );

  const benefitOptionsForRow = useMemo(() => {
    const base = isGFE ? benefitOptionsGFE : benefitOptions;
    const isPreventiveAvailable = rows.some(
      (r) => r.cptCode === row.cptCode && r.preventive
    );
    return isPreventiveAvailable ? [...base, preventiveOption] : base;
  }, [isGFE, rows, row.cptCode]);

  const denefitValue = useMemo(() => {
    const rowsWithSameCPTCode = rows.filter((r) => r.cptCode === row.cptCode);
    const hasPreventiveSelectionType = rowsWithSameCPTCode.some(
      (r) => r.benefitSelectionType === "PREVENTIVE"
    );
    const hasAbnSelectionType = rowsWithSameCPTCode.some(
      (r) => r.benefitSelectionType === "REQUIRES_ABN"
    );

    if (hasPreventiveSelectionType) return "PREVENTIVE";
    if (hasAbnSelectionType) return "REQUIRES_ABN";
    return row.benefitSelectionType || null;
  }, [row.benefitSelectionType, rows]);

  // Calculate provider quantity warnings - uses committedQuantity to avoid
  // flickering while user is still editing the quantity input
  const providerQuantityWarnings = useMemo(() => {
    // Filter rows with the same CPT code
    const rowsWithSameCptCode = rows.filter((r) => r.cptCode === row.cptCode);

    // Group by selectedProvider.id and count
    const providerCounts: Record<string, { name: string; count: number }> = {};

    rowsWithSameCptCode.forEach((r) => {
      const providerId = r.selectedProvider.id;
      if (!providerCounts[providerId]) {
        providerCounts[providerId] = {
          name: r.selectedProvider.name,
          count: 0,
        };
      }
      providerCounts[providerId].count += 1;
    });

    // Find providers with lower quantity than what's displayed
    const warnings: string[] = [];
    const displayedQuantity = committedQuantity || row.quantity || 0;

    Object.values(providerCounts).forEach(({ name, count }) => {
      if (count < displayedQuantity) {
        warnings.push(
          `For ${name} the procedure is included ${
            count === 1 ? "only once" : `only ${count} times`
          }`
        );
      }
    });

    return warnings;
  }, [rows, row.cptCode, committedQuantity, row.quantity]);

  const onChange = (e: InputNumberValueChangeParams) => {
    setQuantity(e.value);

    debouncedQuantityUpdate(e.value);
  };

  const onValueChange = (e: InputNumberValueChangeParams) => {
    if (e.value === null) {
      debouncedQuantityUpdate(0);
    }
  };

  const onQuantityBlur = () => {
    setCommittedQuantity(quantity);
  };

  const onBenefitChange = (e: { value: string | null }) => {
    // Update all rows with the same CPT code to have the same benefit selection
    const newRows = [...rows].map((r) => {
      if (r.cptCode === row.cptCode) {
        return {
          ...r,
          benefitSelectionType: e.value,
        };
      }
      return r;
    });
    dispatch(setRows(newRows));
  };

  const onDescriptionChange = (e: any) => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    if (!newDescription.length) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  };

  const onBlurDescription = () => {
    if (row.description.trim() === description.trim()) return;

    const rowsCopy = [...rows];
    const newRows = rowsCopy.map((aRow) => {
      if (
        aRow.cptCode === row.cptCode &&
        aRow.procedureId === row.procedureId
      ) {
        return {
          ...aRow,
          description: description.trim(),
        };
      }
      return aRow;
    });
    dispatch(setRows(newRows));
  };

  return (
    <div className="flex flex-column border-right-1 border-300">
      <div className="flex flex-row gap-4 align-items-center">
        <span className="flex-2 font-bold">
          {row.cptCode} - {row.description}
        </span>
        <Button
          className="p-button-link p-0"
          style={{ minWidth: "fit-content" }}
          onClick={(e) => {
            op?.current?.show(e, e.currentTarget);
          }}
        >
          <p className="small-text">Edit</p>
        </Button>
        <OverlayPanel
          ref={op}
          style={{
            width: "500px",
            maxWidth: "100%",
          }}
          dismissable={false}
        >
          <div className="flex flex-column gap-1 w-full align-items-end">
            <InputTextarea
              value={description}
              defaultValue={row.description}
              autoResize
              rows={1}
              style={{ maxWidth: "100%", width: "100%", minWidth: "100%" }}
              onChange={onDescriptionChange}
            />
            {showError && (
              <span className="text-sm" style={{ color: "#e24c4c" }}>
                CPT description can not be empty
              </span>
            )}
            <div className="flex flex-row gap-2">
              <Button
                onClick={(e) => {
                  op?.current?.toggle(e);
                  setDescription(row.description);
                  setShowError(false);
                }}
                className="mt-2"
              >
                <p className="small-text">Close</p>
              </Button>
              <Button
                onClick={(e) => {
                  onBlurDescription();
                  op?.current?.toggle(e);
                }}
                disabled={showError}
                className="mt-2"
              >
                <p className="small-text">Save</p>
              </Button>
            </div>
          </div>
        </OverlayPanel>
      </div>
      {row.notAvailableForPayer && (
        <span
          className="small-text text-orange-500 mt-1"
          data-testid="cptNotAvailableForPayer"
        >
          Not available for this payer. Amount and provider are carried over
          from the previous payer.
        </span>
      )}
      <div
        className="flex flex-1 mt-3 flex-row"
        style={{ alignItems: "center" }}
      >
        <span className="mr-2">Qty:</span>
        <InputNumber
          value={quantity}
          inputStyle={{ width: "65px" }}
          inputId="cptCodeQuantity"
          data-testid="cptCodeQuantity_GFEServiceDetails"
          onChange={onChange}
          onValueChange={onValueChange}
          onBlur={onQuantityBlur}
          min={0}
          max={200}
        />

        <Dropdown
          className="ml-4"
          value={denefitValue}
          options={benefitOptionsForRow}
          onChange={onBenefitChange}
          placeholder="Default Benefit"
          style={{ width: "250px" }}
          data-testid="benefitSelection_GFEServiceDetails"
        />
      </div>
      {providerQuantityWarnings.length > 0 && (
        <div className="flex flex-column gap-1 mt-2">
          {providerQuantityWarnings.map((warning, index) => (
            <span key={index} className="small-text text-orange-500">
              {warning}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
